import React, { ReactElement } from 'react';

import { Capacitor } from '@capacitor/core';
import { ListItem, SvgIcon } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { isNil } from 'ramda';

import styles from './SettingCard.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export interface ISetting {
	className?: string;
	type: 'setting';
	icon?: React.ElementType;
	title: string;
	onClick?: () => void;
	id?: string;
	trailingText?: string | null;
	trailingComponent?: ReactElement;
	disabled?: boolean;
	loading?: boolean;
	subtitle?: string;
	red?: boolean;
	noChevron?: boolean;
	chevronIcon?: React.ElementType;
	chevronComponent?: ReactElement;
	condition?: boolean;
	comingSoon?: boolean;
	hidden?: boolean;
	orderPosition?: 'first' | 'last' | 'only' | 'middle';
}

export const SettingCard: React.FC<ISetting> = ({
	icon,
	disabled,
	onClick,
	subtitle,
	title,
	trailingText,
	trailingComponent,
	orderPosition = 'only',
	id,
	loading,
	red = false,
	noChevron = true,
	chevronIcon,
	chevronComponent,
	comingSoon = false,
	hidden = false,
	className,
}) => {
	const settingCardClasses = [
		styles['setting-card'],
		red ? styles['setting-card--red'] : '',
		!onClick ? styles['setting-card--no-action'] : '',
		className,
		styles[`setting-card-${orderPosition}`],
		isCapacitor && styles['no-hover'],
	];

	if (hidden) {
		return null;
	}

	const Chevron: React.FC = () => {
		if (noChevron && !chevronIcon && !chevronComponent) {
			return null;
		}
		if (chevronIcon) {
			return (
				<SvgIcon
					component={chevronIcon}
					className={styles['setting-card__trailing-icon']}
				/>
			);
		}
		if (chevronComponent) {
			return chevronComponent;
		}
		return (
			<ChevronRightIcon
				classes={{ root: styles['setting-card__trailing-icon'] }}
			/>
		);
	};

	return (
		<ListItem
			id={id}
			button
			onClick={onClick ?? (() => {})}
			disableRipple={!onClick}
			disabled={disabled}
			classes={{ root: settingCardClasses.join(' ') }}
		>
			{!isNil(icon) && (
				<div className={styles['setting-card__icon']}>
					<SvgIcon component={icon} />
				</div>
			)}
			<div className={styles['body']}>
				<div className={styles['title']}>{title}</div>
				{subtitle && (
					<div className={styles['subtitle']}>{subtitle}</div>
				)}
			</div>

			<div className={styles['setting-card__trailing']}>
				{trailingComponent ? (
					trailingComponent
				) : (
					<div className={styles['setting-card__trailing-text']}>
						{loading ? 'Loading...' : trailingText}
					</div>
				)}
			</div>
			<Chevron />
			{comingSoon && (
				<div className={styles['setting-card__coming-soon']}>
					Coming soon
				</div>
			)}
		</ListItem>
	);
};

export default SettingCard;
